import { Component, Input, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AlertService } from 'src/app/shared/alert/shared/alert.service';
import { AlertTypes } from 'src/app/shared/alert/shared/alert-types';
import { ConfirmationDialogComponent } from 'src/app/shared/confirmation-dialog/confirmation-dialog.component';
import {
  BatteryConfigDataSet,
  BatteryLoadConfigDataType,
  BatteryLoadConfigValue,
  BatteryType,
  FuelcellBatteryConfigData,
  FuelcellChannelAddress,
  batteryTypeLabels,
  getBatteryLoadConfigDataTypeString,
  getBatteryLoadConfigDataTypeUnit
} from '../../../shared/interfaces/fuelcell-data.interface';
import { FuelcellExpectation } from '../../../shared/functions/fuelcell-expectation';
import { FuelcellService } from '../../../shared/services/fuelcell.service';

/** Editable value of one battery load parameter */
interface LoadConfigRow {
  type: BatteryLoadConfigDataType;
  label: string;
  unit: string;
  data: BatteryConfigDataSet;
  value: number | null;
}

/**
 * Battery configuration of the fuel cell (DE type 143): battery type and the load
 * parameters with their limits.
 *
 * The parameters and their limits depend on the battery type. After changing the
 * type (or resetting the configuration) the data is therefore read once more, and
 * editing is only released again with this new data. As long as the fuel cell has
 * reported neither the sent values nor newer data, the user is asked to refresh later.
 */
@Component({
  selector: 'avs-fuelcell-battery-settings',
  templateUrl: './fuelcell-battery-settings.component.html',
  styleUrls: ['./fuelcell-battery-settings.component.css']
})
export class FuelcellBatterySettingsComponent implements OnChanges {
  @Input() target?: FuelcellChannelAddress;

  public readonly batteryTypes = batteryTypeLabels;

  public data?: FuelcellBatteryConfigData;
  /** Editing is only released once the current data has been read */
  public isLoaded: boolean = false;
  /** Editing stays locked from sending a command until the next successful read */
  public isBusy: boolean = false;
  /** Notice while sending and until the successful read */
  public busyMessage: string = '';
  public information: string = '';
  /** Open after a command until the fuel cell reports the sent values or newer data */
  private pendingExpectation?: FuelcellExpectation<FuelcellBatteryConfigData>;
  public selectedBatType?: BatteryType;
  public rows: LoadConfigRow[] = [];
  /** The inputs are collapsed by default */
  public isBatTypeExpanded: boolean = false;
  public isLoadConfigExpanded: boolean = false;

  constructor(
    private fuelcellService: FuelcellService,
    private alertService: AlertService,
    private dialog: MatDialog
  ) { }

  /** Another fuel cell: an open expectation does not apply to it */
  public ngOnChanges(): void {
    this.pendingExpectation = undefined;
    this.load();
  }

  public get isEditable(): boolean {
    return this.isLoaded && !this.isBusy && this.isConfigAvailable;
  }

  /** Without a reported configuration the device does not support battery parameters */
  public get isConfigAvailable(): boolean {
    return this.data?.batteryConfig != null;
  }

  /** Only after reading it is certain that the device does not deliver battery parameters */
  public get isConfigUnavailable(): boolean {
    return this.isLoaded && !this.isConfigAvailable;
  }

  /** Without a reported configuration the current battery type is unknown */
  public get currentBatType(): BatteryType | undefined {
    return this.data?.batteryConfig?.batType;
  }

  public getBatteryTypeString(batType?: BatteryType): string {
    return this.batteryTypes.find(batteryType => batteryType.type === batType)?.label ?? 'k.A.';
  }

  /** Without a range (min = max) the parameter cannot be adjusted */
  public isValueFixed(row: LoadConfigRow): boolean {
    return row.data.minVal === row.data.maxVal;
  }

  public isValueValid(row: LoadConfigRow): boolean {
    return row.value != null && !isNaN(row.value) && row.value >= row.data.minVal && row.value <= row.data.maxVal;
  }

  public isValueChanged(row: LoadConfigRow): boolean {
    return row.value !== row.data.val;
  }

  public get changedRows(): LoadConfigRow[] {
    return this.rows.filter(row => this.isValueChanged(row));
  }

  public get isLoadConfigSendable(): boolean {
    const changedRows = this.changedRows;
    return changedRows.length > 0 && changedRows.every(row => this.isValueValid(row));
  }

  /**
   * The previous data stays visible (greyed) until the new data is read; a successful read
   * releases editing. An open expectation after a command stays, with its notice, until
   * the fuel cell reports the sent values or newer data (by the timestamp of the fuel cell).
   */
  public load(): void {
    this.isLoaded = false;
    if (!this.target) {
      this.pendingExpectation = undefined;
      this.information = '';
      this.applyData(undefined);
      this.release();
      return;
    }

    this.fuelcellService.getBatteryConfigData(this.target).subscribe({
      next: data => {
        this.applyData(data);
        this.release();
        this.isLoaded = true;
        this.resolveExpectation(data);
      },
      // The error message itself is already shown by the HttpService.
      error: () => {
        this.applyData(undefined);
        this.failRead('Die Batteriekonfiguration konnte nicht gelesen werden.');
      }
    });
  }

  private resolveExpectation(data: FuelcellBatteryConfigData): void {
    if (this.pendingExpectation && !this.pendingExpectation.isResolved(data)) {
      this.information = 'Die Brennstoffzelle hat die neuen Werte noch nicht gemeldet. Bitte später aktualisieren.';
      return;
    }
    this.pendingExpectation = undefined;
    this.information = '';
  }

  public onChangeBatteryTypeClicked(): void {
    if (!this.target || !this.isEditable || this.selectedBatType === undefined) { return; }
    const batType = this.selectedBatType;
    this.askConfirmation('Batterietyp ändern?',
      `Batterietyp auf "${this.getBatteryTypeString(batType)}" ändern? Die Ladeparameter werden danach neu gelesen.`,
      'Ja')
      .subscribe(isConfirmed => {
        if (!isConfirmed) { return; }
        this.send(() => this.fuelcellService.changeBatteryType(this.target!, batType),
          'Der Batterietyp wurde an die Brennstoffzelle gesendet', data => data.batteryConfig?.batType === batType);
      });
  }

  /** Reset discards all load parameters, therefore it has to be confirmed twice */
  public onResetClicked(): void {
    if (!this.target || !this.isEditable) { return; }
    this.askConfirmation('Batteriekonfiguration zurücksetzen?',
      'Die Batteriekonfiguration der Brennstoffzelle auf Werkseinstellungen zurücksetzen?',
      'Ja')
      .subscribe(isConfirmed => {
        if (!isConfirmed) { return; }
        this.askConfirmation('Wirklich zurücksetzen?',
          'Alle eingestellten Ladeparameter der Brennstoffzelle werden durch die Werkseinstellungen ersetzt. ' +
          'Dies kann nicht rückgängig gemacht werden.',
          'Zurücksetzen')
          .subscribe(isConfirmedAgain => {
            if (!isConfirmedAgain) { return; }
            this.send(() => this.fuelcellService.resetBatteryConfig(this.target!),
              'Die Batteriekonfiguration wurde zurückgesetzt');
          });
      });
  }

  public onSaveLoadConfigClicked(): void {
    if (!this.target || !this.isEditable || !this.isLoadConfigSendable) { return; }
    const changedRows = this.changedRows;
    const loadConfig: BatteryLoadConfigValue[] = changedRows
      .map(row => ({ configDataType: row.type, configDataValue: row.value! }));

    this.send(() => this.fuelcellService.changeBatteryLoadConfig(this.target!, loadConfig),
      'Die Ladeparameter wurden an die Brennstoffzelle gesendet', data =>
      loadConfig.every(value =>
        data.batteryConfig?.configData.find(item => item.type === value.configDataType)?.data?.val === value.configDataValue));
  }

  public onResetRowClicked(row: LoadConfigRow): void {
    row.value = row.data.defVal;
  }

  private askConfirmation(title: string, message: string, confirmButtonText: string): Observable<boolean> {
    return this.dialog.open(ConfirmationDialogComponent, {
      data: { title: title, message: message, confirmButtonText: confirmButtonText, cancelButtonText: 'Abbrechen' }
    }).afterClosed();
  }

  /** Command: lock editing until the data is read again successfully */
  private send(command: () => Observable<any>, successMessage: string,
    isExpected?: (data: FuelcellBatteryConfigData) => boolean): void {
    this.isBusy = true;
    this.busyMessage = 'Befehl wird gesendet …';
    this.information = '';
    const expectation = isExpected ? new FuelcellExpectation(isExpected, data => [data.time], this.data) : undefined;
    command().subscribe({
      next: () => {
        this.alertService.alert(successMessage, AlertTypes.info);
        this.pendingExpectation = expectation;
        this.busyMessage = 'Befehl gesendet, Daten werden neu gelesen …';
        this.load();
      },
      // After a failed command the current data is read again (the error itself is shown by the HttpService)
      error: () => this.load()
    });
  }

  private release(): void {
    this.isBusy = false;
    this.busyMessage = '';
  }

  /** After a command the section stays locked until a read succeeds; otherwise the read error is shown */
  private failRead(errorMessage: string): void {
    if (this.isBusy) {
      this.busyMessage = 'Die Daten konnten nicht neu gelesen werden. Zum Freigeben der Bearbeitung bitte manuell aktualisieren.';
    } else {
      this.information = errorMessage;
    }
  }

  private applyData(data?: FuelcellBatteryConfigData): void {
    this.data = data;
    this.selectedBatType = data?.batteryConfig?.batType;
    this.rows = (data?.batteryConfig?.configData ?? [])
      .filter(item => item.data != null)
      .map(item => ({
        type: item.type,
        label: getBatteryLoadConfigDataTypeString(item.type),
        unit: getBatteryLoadConfigDataTypeUnit(item.type),
        data: { ...item.data! },
        value: item.data!.val
      }));
  }
}
